import { useEffect, useMemo, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, RotateCcw, Check, X, Lock } from "lucide-react";

/**
 * Permission groups (used only for UI grouping).
 * Backend is the source of truth on the actual permission keys.
 */
const GROUPS = [
  {
    title: "Clients",
    perms: [
      ["view_clients", "View clients"],
      ["add_clients", "Add new clients"],
      ["edit_clients", "Edit client details"],
      ["delete_clients", "Archive / delete clients"],
    ],
  },
  {
    title: "Contacts",
    perms: [
      ["view_contacts", "View contacts"],
      ["add_contacts", "Add contacts"],
      ["edit_contacts", "Edit contacts"],
      ["delete_contacts", "Archive / delete contacts"],
    ],
  },
  {
    title: "Deals & Pipeline",
    perms: [
      ["view_deals", "View deals"],
      ["add_deals", "Create deals"],
      ["edit_deals", "Edit deal fields"],
      ["delete_deals", "Archive / delete deals"],
      ["move_deal_stage", "Move deals across stages"],
      ["reassign_deal", "Reassign deal owner"],
    ],
  },
  {
    title: "Activities & Memory",
    perms: [
      ["view_activities", "View activities"],
      ["add_activities", "Log meeting / call notes"],
      ["edit_activities", "Edit activities"],
      ["delete_activities", "Delete activities"],
    ],
  },
  {
    title: "Follow-ups",
    perms: [
      ["view_followups", "View follow-ups"],
      ["add_followups", "Create follow-ups"],
      ["edit_followups", "Edit follow-ups"],
      ["complete_followups", "Mark follow-ups complete"],
    ],
  },
  {
    title: "Reports",
    perms: [
      ["view_reports", "View own reports"],
      ["view_team_reports", "View team performance"],
      ["export_reports", "Export reports"],
    ],
  },
  {
    title: "Products & Services",
    perms: [
      ["view_products", "View knowledge base"],
      ["add_products", "Add products / services"],
      ["edit_products", "Edit products / services"],
      ["delete_products", "Delete products / services"],
    ],
  },
  {
    title: "AI & Files",
    perms: [
      ["upload_files", "Upload files / cards"],
      ["use_ai_extract", "Use AI extraction"],
      ["confirm_ai_updates", "Confirm AI suggestions"],
    ],
  },
  {
    title: "Admin",
    perms: [
      ["manage_users", "Manage users"],
      ["manage_permissions", "Manage permissions"],
      ["view_audit_logs", "View audit logs"],
      ["export_data", "Export company data"],
      ["bulk_import", "Bulk import customers"],
    ],
  },
];

/**
 * Effective resolution:
 *   effective = (role_default ∪ overrides) − revoked
 */
function resolveEffective(rolePerms, overrides, revoked) {
  const set = new Set(rolePerms || []);
  (overrides || []).forEach((p) => set.add(p));
  (revoked || []).forEach((p) => set.delete(p));
  return set;
}

export default function PermissionEditor({ user, open, onOpenChange, onSaved }) {
  const [matrix, setMatrix] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [revoked, setRevoked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setOverrides([...(user.perm_overrides || [])]);
    setRevoked([...(user.perm_revoked || [])]);
    api.get("/permissions/matrix").then((r) => setMatrix(r.data)).catch(() => {});
  }, [open, user]);

  const rolePerms = useMemo(
    () => (matrix?.role_perms?.[user?.role] || []),
    [matrix, user]
  );
  const effective = useMemo(
    () => resolveEffective(rolePerms, overrides, revoked),
    [rolePerms, overrides, revoked]
  );

  const togglePerm = (perm) => {
    const isInRole = rolePerms.includes(perm);
    const isEffective = effective.has(perm);
    const next = !isEffective;
    if (next) {
      // Want to grant
      setRevoked((r) => r.filter((p) => p !== perm));
      if (!isInRole) setOverrides((o) => (o.includes(perm) ? o : [...o, perm]));
    } else {
      // Want to remove
      setOverrides((o) => o.filter((p) => p !== perm));
      if (isInRole) setRevoked((r) => (r.includes(perm) ? r : [...r, perm]));
    }
  };

  const reset = () => { setOverrides([]); setRevoked([]); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { perm_overrides: overrides, perm_revoked: revoked });
      toast.success("Permissions updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  if (!user) return null;

  const customCount = overrides.length + revoked.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/60">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight truncate">
                Permissions · {user.name}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-1">
                {user.email} · base role: <span className="capitalize font-semibold">{user.role}</span>
                {customCount > 0 && (
                  <>
                    {" · "}
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200">
                      {customCount} custom
                    </Badge>
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <Tabs defaultValue={GROUPS[0].title}>
            <TabsList className="rounded-2xl flex-wrap h-auto p-1 mb-4">
              {GROUPS.map((g) => (
                <TabsTrigger key={g.title} value={g.title} className="rounded-xl text-xs" data-testid={`perm-tab-${g.title.toLowerCase().replace(/\W/g, "-")}`}>
                  {g.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {GROUPS.map((g) => (
              <TabsContent key={g.title} value={g.title} className="space-y-2">
                {g.perms.map(([perm, label]) => {
                  const inRole = rolePerms.includes(perm);
                  const isOverride = overrides.includes(perm);
                  const isRevoked = revoked.includes(perm);
                  const granted = effective.has(perm);
                  return (
                    <div
                      key={perm}
                      className={`flex items-center gap-3 p-3.5 rounded-2xl border transition ${
                        granted ? "border-border/60 bg-card" : "border-border/40 bg-muted/40"
                      }`}
                      data-testid={`perm-row-${perm}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{label}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mt-0.5">{perm}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {inRole && !isRevoked && !isOverride && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-primary-50 text-primary-700 border-primary-200">
                            <Lock className="h-2.5 w-2.5 mr-0.5" /> Role
                          </Badge>
                        )}
                        {isOverride && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border-emerald-200">
                            <Check className="h-2.5 w-2.5 mr-0.5" /> Granted
                          </Badge>
                        )}
                        {isRevoked && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-red-50 text-red-700 border-red-200">
                            <X className="h-2.5 w-2.5 mr-0.5" /> Revoked
                          </Badge>
                        )}
                        <Switch
                          checked={granted}
                          onCheckedChange={() => togglePerm(perm)}
                          data-testid={`perm-toggle-${perm}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border/60 flex-row sm:justify-between items-center gap-3 bg-muted/20">
          <Button variant="ghost" size="sm" onClick={reset} className="rounded-xl tap" data-testid="perm-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to role default
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-2xl tap" data-testid="perm-save">
              {saving ? "Saving…" : "Save permissions"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
